import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { TFunction } from 'i18next'

import { deleteFile, downloadFile, updateFile } from '../../../api/files'
import { IFile } from '../files-model'

import styles from './files-table.module.scss'

interface IFileRowProps {
    file: IFile
    refetch: () => void
}

const getStatusLabel = (
    status: 'ready' | 'indexing' | 'failed',
    t: TFunction
) => {
    switch (status) {
        case 'ready':
            return t('indexed')
        case 'indexing':
            return t('awaiting_indexation')
        case 'failed':
            return t('indexation_failed')
        default:
            return status
    }
}

const formatDate = (date: string) =>
    new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(new Date(date))

function FileRow({ file, refetch }: IFileRowProps) {
    const { t } = useTranslation()
    const inputRef = useRef<HTMLInputElement>(null)

    const handleUpdate = () => {
        inputRef.current?.click()
    }

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        try {
            const selectedFile = event.target.files?.[0]

            if (!selectedFile) {
                return
            }

            const formData = new FormData()
            formData.append('file', selectedFile)

            await updateFile(file._id, formData)

            event.target.value = ''
            refetch()
        } catch {
            toast(`${t('file_update_failed')}`, {
                type: 'error',
            })
        }
    }

    const handleDelete = async () => {
        try {
            await deleteFile(file._id)
            refetch()
        } catch {
            toast(`${t('file_delete_failed')}`, {
                type: 'error',
            })
        }
    }

    const handleDownload = async () => {
        try {
            await downloadFile(file._id, file.filename)
        } catch {
            toast(`${t('file_download_failed')}`, {
                type: 'error',
            })
        }
    }

    return (
        <tr>
            <td>{file.filename}</td>

            <td>
                <div className={styles.filesTable__date}>
                    {formatDate(file.updated_at)}
                </div>

                <div className={styles.filesTable__email}>
                    {file.updated_by_email}
                </div>
            </td>

            <td>
                <div>{getStatusLabel(file.status, t)}</div>

                {file.status === 'failed' && file.error_message && (
                    <div className={styles.filesTable__error}>
                        {file.error_message}
                    </div>
                )}
            </td>

            <td>
                <div className={styles.filesTable__actions}>
                    <button onClick={handleDownload}>{t('download')}</button>

                    <button onClick={handleUpdate}>{t('update')}</button>

                    <button onClick={handleDelete}>{t('delete')}</button>

                    <input
                        ref={inputRef}
                        type="file"
                        hidden
                        onChange={handleFileChange}
                    />
                </div>
            </td>
        </tr>
    )
}

export default FileRow
